import React, { useRef, useId, useCallback } from 'react';
import { createPortal } from 'react-dom';
import '../../assets/components/Tooltip.css';

const GAP = 6;

const Tooltip = ({ children, label, placement = 'top' }) => {
    const tooltipRef = useRef(null);
    const wrapperRef = useRef(null);
    const tooltipId = useId();

    const show = useCallback(() => {
        const trigger = wrapperRef.current;
        const tooltip = tooltipRef.current;
        if (!trigger || !tooltip || !label) return;

        tooltip.showPopover();

        const triggerRect = (trigger.firstElementChild || trigger).getBoundingClientRect();
        const tooltipRect = tooltip.getBoundingClientRect();

        let top;
        let resolvedPlacement = placement;

        if (placement === 'top') {
            top = triggerRect.top - tooltipRect.height - GAP;
            if (top < 4) {
                resolvedPlacement = 'bottom';
                top = triggerRect.bottom + GAP;
            }
        } else {
            top = triggerRect.bottom + GAP;
            if (top + tooltipRect.height > window.innerHeight - 4) {
                resolvedPlacement = 'top';
                top = triggerRect.top - tooltipRect.height - GAP;
            }
        }

        const left = Math.max(
            4,
            Math.min(
                triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2,
                window.innerWidth - tooltipRect.width - 4
            )
        );

        const arrowLeft = Math.max(
            10,
            Math.min(
                triggerRect.left + triggerRect.width / 2 - left,
                tooltipRect.width - 10
            )
        );

        tooltip.style.left = `${Math.round(left)}px`;
        tooltip.style.top = `${Math.round(top)}px`;
        tooltip.style.setProperty('--arrow-left', `${Math.round(arrowLeft)}px`);
        tooltip.dataset.placement = resolvedPlacement;
    }, [label, placement]);

    const hide = useCallback(() => {
        tooltipRef.current?.hidePopover();
    }, []);

    if (!label) return children;

    return (
        <>
            <span
                ref={wrapperRef}
                className="tooltip__trigger"
                onMouseEnter={show}
                onMouseLeave={hide}
                aria-describedby={tooltipId}
            >
                {children}
            </span>
            {createPortal(
                <div
                    ref={tooltipRef}
                    id={tooltipId}
                    role="tooltip"
                    popover="manual"
                    className="tooltip"
                >
                    {label}
                </div>,
                document.body
            )}
        </>
    );
};

export default Tooltip;
